import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Package, Boxes, Factory, Truck, Users,
  UserCog, Receipt, Wallet, HandCoins, BarChart3, Settings, LogOut, Menu, X,
  Bell, Globe, Sprout,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, bn: "ড্যাশবোর্ড", en: "Dashboard", mod: "dashboard" },
  { to: "/admin/orders", icon: ShoppingCart, bn: "অনলাইন অর্ডার", en: "Orders", mod: "orders" },
  { to: "/admin/products", icon: Package, bn: "পণ্য", en: "Products", mod: "products" },
  { to: "/admin/stock", icon: Boxes, bn: "স্টক / ইনভেন্টরি", en: "Stock", mod: "stock" },
  { to: "/admin/production", icon: Factory, bn: "প্রোডাকশন", en: "Production", mod: "production" },
  { to: "/admin/purchases", icon: Truck, bn: "ধান ক্রয়", en: "Purchases", mod: "purchases" },
  { to: "/admin/suppliers", icon: UserCog, bn: "সরবরাহকারী", en: "Suppliers", mod: "purchases" },
  { to: "/admin/customers", icon: Users, bn: "কাস্টমার", en: "Customers", mod: "customers" },
  { to: "/admin/sales", icon: Receipt, bn: "বিক্রয় (POS)", en: "Sales", mod: "sales" },
  { to: "/admin/expenses", icon: Wallet, bn: "খরচ", en: "Expenses", mod: "expenses" },
  { to: "/admin/due", icon: HandCoins, bn: "দেনা-পাওনা", en: "Due", mod: "due" },
  { to: "/admin/reports", icon: BarChart3, bn: "রিপোর্ট ও লাভ", en: "Reports", mod: "reports" },
  { to: "/admin/settings", icon: Settings, bn: "সেটিংস", en: "Settings", mod: "settings" },
];

const ROLE_BN = {
  super_admin: "সুপার অ্যাডমিন", manager: "ম্যানেজার", sales_staff: "সেলস স্টাফ",
  production_staff: "প্রোডাকশন স্টাফ", accountant: "হিসাবরক্ষক",
};

export default function AdminLayout() {
  const { user, logout, loading, changePassword } = useAuth();
  const { lang, toggle, t } = useLang();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [modules, setModules] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });

  useEffect(() => {
    if (!loading && (!user || user.role === "customer")) { nav("/admin/login"); return; }
    if (user) {
      api.get("/auth/permissions").then((r) => setModules(r.data.modules)).catch(() => {});
      loadNotifs();
    }
  }, [user, loading]); // eslint-disable-line

  const loadNotifs = () => api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  const allowed = (mod) => modules.includes("*") || modules.includes(mod);
  const unread = notifs.filter((n) => !n.read).length;

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-stone-500">লোড হচ্ছে...</div>;

  if (user.must_change_password) {
    const submitPw = async () => {
      if (pw.next.length < 8) return toast.error("নতুন পাসওয়ার্ড কমপক্ষে ৮ অক্ষরের হতে হবে");
      if (pw.next !== pw.confirm) return toast.error("পাসওয়ার্ড মেলেনি");
      try { await changePassword(pw.current, pw.next); toast.success("পাসওয়ার্ড পরিবর্তিত হয়েছে"); }
      catch (e) { toast.error(e.response?.data?.detail || "ত্রুটি"); }
    };
    const pwInp = "w-full h-11 px-3 rounded-xl border border-stone-300";
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#14281D] p-4">
        <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-3">
          <h2 className="text-xl font-bold text-[#1E3A2B]">প্রথম লগইন — পাসওয়ার্ড পরিবর্তন করুন</h2>
          <p className="text-sm text-stone-500">নিরাপত্তার জন্য ডিফল্ট পাসওয়ার্ড পরিবর্তন করা বাধ্যতামূলক।</p>
          <input type="password" placeholder="বর্তমান পাসওয়ার্ড" className={pwInp} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="cp-current" />
          <input type="password" placeholder="নতুন পাসওয়ার্ড (কমপক্ষে ৮ অক্ষর)" className={pwInp} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="cp-new" />
          <input type="password" placeholder="নতুন পাসওয়ার্ড আবার লিখুন" className={pwInp} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="cp-confirm" />
          <button onClick={submitPw} className="w-full bg-[#1E3A2B] text-white h-11 rounded-xl font-medium" data-testid="cp-submit">পরিবর্তন করুন</button>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="w-full text-sm text-stone-500">লগআউট</button>
        </div>
      </div>
    );
  }

  const navItems = NAV.filter((n) => allowed(n.mod));

  const SidebarContent = () => (
    <>
      <div className="flex items-center gap-2 px-4 h-16 border-b border-white/10">
        <div className="w-9 h-9 rounded-xl bg-[#CA8A04] flex items-center justify-center text-[#1E3A2B]"><Sprout size={20} /></div>
        <div className="leading-tight">
          <div className="font-bold text-white text-sm">M.D.Rice Mill</div>
          <div className="text-[10px] text-emerald-300 font-latin">ADMIN PANEL</div>
        </div>
      </div>
      <nav className="flex-1 overflow-auto p-2 space-y-0.5 no-scrollbar">
        {navItems.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.mod}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-[#CA8A04] text-white" : "text-emerald-100 hover:bg-white/10"}`}>
            <n.icon size={18} /> {t(n.bn, n.en)}
          </NavLink>
        ))}
      </nav>
      <div className="p-2 border-t border-white/10">
        <button onClick={() => { logout(); nav("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-200 hover:bg-red-500/20" data-testid="admin-logout">
          <LogOut size={18} /> {t("লগআউট", "Logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F5] flex">
      <aside className="hidden lg:flex flex-col w-64 bg-[#14281D] fixed inset-y-0 z-40">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#14281D] flex flex-col"><SidebarContent /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-stone-200 h-16 flex items-center gap-3 px-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu size={24} /></button>
          <div className="font-semibold text-[#1E3A2B]">{ROLE_BN[user.role]} প্যানেল</div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={toggle} className="flex items-center gap-1 h-9 px-3 rounded-lg border border-stone-300 text-sm font-medium" data-testid="lang-toggle">
              <Globe size={15} /> {lang === "bn" ? "EN" : "বাং"}
            </button>
            <div className="relative">
              <button onClick={() => { setShowNotif(!showNotif); if (!showNotif) api.put("/admin/notifications/read").then(loadNotifs); }} className="relative w-9 h-9 flex items-center justify-center rounded-lg border border-stone-300" data-testid="notif-btn">
                <Bell size={18} />
                {unread > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto bg-white rounded-xl border border-stone-200 shadow-lg z-50">
                  <div className="p-3 border-b border-stone-100 font-semibold text-sm">নোটিফিকেশন</div>
                  {notifs.length === 0 ? <div className="p-4 text-sm text-stone-400 text-center">কিছু নেই</div> :
                    notifs.slice(0, 20).map((n) => (
                      <div key={n.id} className="p-3 border-b border-stone-50 text-sm text-stone-700">{n.message}
                        <div className="text-[10px] text-stone-400">{new Date(n.created_at).toLocaleString("bn-BD")}</div>
                      </div>
                    ))}
                </div>
              )}
            </div>
            <div className="w-9 h-9 rounded-full bg-[#1E3A2B] text-white flex items-center justify-center font-semibold text-sm">{user.name[0]}</div>
          </div>
        </header>
        <main className="p-4 sm:p-6"><Outlet context={{ reloadNotifs: loadNotifs }} /></main>
      </div>
    </div>
  );
}
